"use client";

import {
  Cloud,
  CreditCard,
  Database,
  FileText,
  Github,
  Globe,
  HardDrive,
  Heart,
  Leaf,
  Mail,
  MessageSquare,
  Phone,
  Table2,
  Zap,
} from "lucide-react";
import { motion } from "motion/react";
import {
  FadeInUp,
  StaggerContainer,
  StaggerItem,
} from "@/components/ui/motion";
import { useIsMobile } from "@/hooks/use-mobile";

const integrations = [
  { name: "Slack", category: "Communication", icon: MessageSquare },
  { name: "Stripe", category: "Payments", icon: CreditCard },
  { name: "Google Sheets", category: "Productivity", icon: Table2 },
  { name: "Microsoft Teams", category: "Communication", icon: MessageSquare },
  { name: "GitHub", category: "Developer", icon: Github },
  { name: "Brave Search", category: "Automation", icon: Globe },
  { name: "Salesforce", category: "CRM", icon: Cloud },
  { name: "HubSpot", category: "Marketing", icon: Heart },
  { name: "Notion", category: "Productivity", icon: FileText },
  { name: "Airtable", category: "Database", icon: Table2 },
  { name: "Twilio", category: "Communication", icon: Phone },
  { name: "SendGrid", category: "Email", icon: Mail },
  { name: "AWS S3", category: "Storage", icon: HardDrive },
  { name: "PostgreSQL", category: "Database", icon: Database },
  { name: "MongoDB", category: "Database", icon: Leaf },
];

export function Integrations() {
  const isMobile = useIsMobile();

  return (
    <section
      id="integrations"
      className="py-20 lg:py-28 section-elevated relative overflow-hidden"
    >
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-primary/8 rounded-full blur-[100px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-accent/8 rounded-full blur-[80px] pointer-events-none" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
        <FadeInUp className="max-w-2xl mx-auto text-center mb-12">
          <span className="inline-block px-3 py-1.5 mb-4 text-xs font-semibold tracking-wider uppercase bg-primary/10 text-primary rounded-full border border-primary/20">
            Integrations
          </span>
          <h2 className="text-foreground mb-4">
            Connect to <span className="gradient-text">everything</span>
          </h2>
          <p className="text-lg text-muted-foreground">
            200+ pre-built integrations. Plus webhooks and HTTP for anything
            custom.
          </p>
        </FadeInUp>

        <StaggerContainer className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-3 sm:gap-4 max-w-4xl mx-auto">
          {integrations.map((integration) => (
            <StaggerItem key={integration.name}>
              <motion.div
                whileHover={isMobile ? {} : { y: -4, scale: 1.02 }}
                className="group flex flex-col items-center p-3 sm:p-4 rounded-xl bg-card border border-border hover:border-primary/30 hover:shadow-lg hover:shadow-primary/5 transition-all cursor-pointer card-interactive"
              >
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-2 group-hover:bg-primary/20 transition-colors">
                  <integration.icon className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                </div>
                <span className="text-xs sm:text-sm font-medium text-foreground text-center">
                  {integration.name}
                </span>
                <span className="text-[9px] sm:text-[10px] text-muted-foreground">
                  {integration.category}
                </span>
              </motion.div>
            </StaggerItem>
          ))}
        </StaggerContainer>

        <FadeInUp delay={0.3} className="text-center mt-10">
          <p className="text-muted-foreground">
            Don't see what you need? Use our{" "}
            <span className="text-primary font-medium">HTTP node</span> or{" "}
            <span className="text-primary font-medium">webhooks</span> to
            connect anything.
          </p>
        </FadeInUp>
      </div>
    </section>
  );
}
